import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    AlertTriangle,
    Calendar,
    Check,
    CheckCircle,
    ChevronDown,
    Clock,
    Copy,
    LayoutGrid,
    ListChecks,
    Loader2,
    Plus,
    Search,
    Trash2,
    X,
    XCircle,
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { FUNCOES_COM_RODIZIO } from '../../src/lib/departamentos';
import { logUsageEvent } from '../../src/lib/logger';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { estaDisponivel, tempoUtilDecorrido } from '../../src/lib/escalaDisponibilidade';
import { pausaAberta, minutosPausadosNoDia, fmtDuracaoMin, ehPausa } from '../../src/lib/pausaSla';
import SlaAtendimentoBlock from '../CRM/Analytics/blocks/SlaAtendimentoBlock';
import { fmtDecorrido, nivelUrgencia } from './leadsSemAtendimento';
import { HorarioTrabalho, AusenciaTrabalho, SolicitacaoAusencia, Profile } from '../../types';

const DIAS_UTEIS = [1, 2, 3, 4, 5]; // seg-sex
const DIA_LABEL: Record<number, string> = { 0: 'Dom', 1: 'Seg', 2: 'Ter', 3: 'Qua', 4: 'Qui', 5: 'Sex', 6: 'Sáb' };
const DIA_ORDEM = [1, 2, 3, 4, 5, 6, 0]; // seg..dom, mais natural pra uma escala de trabalho

interface DiaEdit {
    ativo: boolean;
    hora_inicio: string;
    hora_fim: string;
    almoco_inicio: string;
    almoco_fim: string;
}

const diaVazio = (): DiaEdit => ({ ativo: false, hora_inicio: '08:00', hora_fim: '18:00', almoco_inicio: '12:00', almoco_fim: '13:00' });

/** AAAA-MM-DD de hoje em Brasília. `toISOString()` dá o dia em UTC, que depois
 * das 21h já é amanhã — a ausência de hoje sumia da lista e "ausente hoje" errava. */
const hojeBrasilia = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

/** Quantos dias para trás a lista de ausências que já passaram alcança. Ausência
 * com data passada vale para o SLA e para a comissão desses dias (o cálculo lê
 * todas na hora), então o gestor precisa enxergar o que cadastrou retroativo. */
const DIAS_AUSENCIAS_PASSADAS = 90;
const hhmm = (v?: string | null) => (v || '').slice(0, 5);

const resumoEscala = (dias: Record<number, DiaEdit>): string => {
    const ativos = DIA_ORDEM.filter(d => dias[d]?.ativo);
    if (ativos.length === 0) return 'Sem escala definida';
    const primeiro = dias[ativos[0]];
    const uniforme = ativos.every(d =>
        dias[d].hora_inicio === primeiro.hora_inicio && dias[d].hora_fim === primeiro.hora_fim
    );
    const rangeLabel = ativos.length === DIA_ORDEM.length
        ? 'Todos os dias'
        : `${DIA_LABEL[ativos[0]]}–${DIA_LABEL[ativos[ativos.length - 1]]}`;
    if (!uniforme) return `${rangeLabel} · Horário variável`;
    return `${rangeLabel} · ${primeiro.hora_inicio}–${primeiro.hora_fim}`;
};

interface AdicionarAusenciaModalProps {
    onClose: () => void;
    onConfirm: (data: string, diaInteiro: boolean, horaInicio: string, horaFim: string, motivo: string) => void;
    salvando: boolean;
}

const AdicionarAusenciaModal: React.FC<AdicionarAusenciaModalProps> = ({ onClose, onConfirm, salvando }) => {
    const [data, setData] = useState(hojeBrasilia());
    const [diaInteiro, setDiaInteiro] = useState(true);
    const [horaInicio, setHoraInicio] = useState('08:00');
    const [horaFim, setHoraFim] = useState('12:00');
    const [motivo, setMotivo] = useState('');

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl" onClick={e => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <h3 className="text-base font-bold text-gray-900 dark:text-white">Adicionar ausência</h3>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={18} /></button>
                </div>
                <div className="px-6 py-4 space-y-4">
                    <div>
                        <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">Data</label>
                        <input type="date" value={data} onChange={e => setData(e.target.value)}
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <input type="checkbox" checked={diaInteiro} onChange={e => setDiaInteiro(e.target.checked)} className="rounded" />
                        Dia inteiro
                    </label>
                    {!diaInteiro && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">Das</label>
                                <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)}
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">Até</label>
                                <input type="time" value={horaFim} onChange={e => setHoraFim(e.target.value)}
                                    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-xs font-bold text-gray-600 dark:text-gray-300 block mb-1">Motivo (opcional)</label>
                        <input value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ex.: Consulta médica"
                            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100" />
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
                    <button
                        onClick={() => onConfirm(data, diaInteiro, horaInicio, horaFim, motivo)}
                        disabled={salvando || !data}
                        className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                    >
                        {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirmar
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

interface PessoaPainelProps {
    pessoa: Profile;
    horarios: HorarioTrabalho[];
    ausencias: AusenciaTrabalho[];
    solicitacoes: SolicitacaoAusencia[];
    onChanged: () => void;
}

const ORIGEM_LABEL: Record<AusenciaTrabalho['origem'], string> = {
    gestor: 'Lançada pelo gestor',
    solicitacao: 'Solicitação aprovada',
    // Não chega a ser exibido — a lista de ausências filtra pausas (ver
    // ausenciasFuturas) —, mas o rótulo existe para o dia em que aparecer.
    pausa: 'Pausa de SLA da própria pessoa',
};

const SOLIC_STATUS_LABEL: Record<string, { label: string; className: string }> = {
    aprovada: { label: 'Aprovada', className: 'text-emerald-600 dark:text-emerald-400' },
    rejeitada: { label: 'Rejeitada', className: 'text-red-600 dark:text-red-400' },
};

const PessoaPainel: React.FC<PessoaPainelProps> = ({ pessoa, horarios, ausencias, solicitacoes, onChanged }) => {
    const { user } = useAuth();
    const { addToast } = useToast();
    const [aberto, setAberto] = useState(false);
    const [showHistorico, setShowHistorico] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [excluindo, setExcluindo] = useState<string | null>(null);
    const [showPassadas, setShowPassadas] = useState(false);

    const [dias, setDias] = useState<Record<number, DiaEdit>>(() => {
        const base: Record<number, DiaEdit> = {};
        for (const d of DIA_ORDEM) {
            const h = horarios.find(x => x.dia_semana === d);
            base[d] = h
                ? { ativo: h.ativo, hora_inicio: hhmm(h.hora_inicio), hora_fim: hhmm(h.hora_fim), almoco_inicio: hhmm(h.almoco_inicio) || '', almoco_fim: hhmm(h.almoco_fim) || '' }
                : diaVazio();
        }
        return base;
    });

    // Pausas de SLA ficam de fora: esta lista é de ausência planejada (o que o
    // gestor cadastra e desfaz). Pausa é micro-evento do dia, várias por dia, e
    // aparece no "Disponível agora" com o total do dia.
    const hoje = hojeBrasilia();
    const ausenciasFuturas = ausencias
        .filter(a => !ehPausa(a) && a.data >= hoje)
        .sort((a, b) => a.data.localeCompare(b.data));

    // Antes só havia a lista de hoje em diante: ausência cadastrada com data passada
    // (ex.: consulta da semana anterior) era gravada e valia no SLA, mas não aparecia
    // em lugar nenhum — parecia que não tinha salvado.
    const limitePassadas = new Date(Date.now() - DIAS_AUSENCIAS_PASSADAS * 86400000)
        .toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
    const ausenciasPassadas = ausencias
        .filter(a => !ehPausa(a) && a.data < hoje && a.data >= limitePassadas)
        .sort((a, b) => b.data.localeCompare(a.data));

    // Decisões já tomadas (aprovada/rejeitada) — a fila em EscalasComercialView só
    // mostra 'pendente'; isso é o rastro de auditoria, não pede ação do gestor hoje.
    const historicoSolicitacoes = solicitacoes
        .filter(s => s.status !== 'pendente')
        .sort((a, b) => (b.decidido_em || '').localeCompare(a.decidido_em || ''));

    const ausenteHoje = ausencias.some(a => !ehPausa(a) && a.data === hoje);
    const semEscala = DIA_ORDEM.every(d => !dias[d].ativo);

    const atualizarDia = (d: number, patch: Partial<DiaEdit>) => setDias(prev => ({ ...prev, [d]: { ...prev[d], ...patch } }));

    const aplicarATodosOsDiasUteis = () => {
        const segunda = dias[1];
        setDias(prev => {
            const novo = { ...prev };
            for (const d of DIAS_UTEIS) {
                novo[d] = { ...segunda };
            }
            return novo;
        });
    };

    const salvar = async () => {
        setSalvando(true);
        try {
            const rows = DIA_ORDEM.map(d => ({
                profile_id: pessoa.id,
                dia_semana: d,
                ativo: dias[d].ativo,
                hora_inicio: dias[d].hora_inicio,
                hora_fim: dias[d].hora_fim,
                almoco_inicio: dias[d].almoco_inicio || null,
                almoco_fim: dias[d].almoco_fim || null,
            }));
            const { error } = await supabase.from('horarios_trabalho').upsert(rows, { onConflict: 'profile_id,dia_semana' });
            if (error) throw error;
            addToast('Escala salva.', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'escalas', acao: 'salvar_escala', origem: 'acao' });
            onChanged();
        } catch (e: any) {
            addToast('Erro ao salvar escala: ' + (e.message || 'erro desconhecido'), 'error');
        } finally {
            setSalvando(false);
        }
    };

    const adicionarAusencia = async (data: string, diaInteiro: boolean, horaInicio: string, horaFim: string, motivo: string) => {
        setSalvando(true);
        try {
            const { error } = await supabase.from('ausencias_trabalho').insert({
                profile_id: pessoa.id,
                data,
                dia_inteiro: diaInteiro,
                hora_inicio: diaInteiro ? null : horaInicio,
                hora_fim: diaInteiro ? null : horaFim,
                motivo: motivo || null,
                criado_por: user?.id,
                origem: 'gestor',
            });
            if (error) throw error;
            const passada = data < hojeBrasilia();
            // Data passada vai para "Já passaram": abre a lista para o gestor ver que salvou.
            if (passada) setShowPassadas(true);
            addToast(passada ? 'Ausência cadastrada em "Já passaram". Ela já conta no SLA desse dia.' : 'Ausência cadastrada.', 'success');
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'escalas', acao: 'cadastrar_ausencia', origem: 'acao' });
            setShowModal(false);
            onChanged();
        } catch (e: any) {
            addToast('Erro ao cadastrar ausência: ' + (e.message || 'erro desconhecido'), 'error');
        } finally {
            setSalvando(false);
        }
    };

    // Mesma linha nas duas listas; a que já passou fica esmaecida, mas continua com a
    // lixeira — é o jeito de corrigir uma ausência lançada errada, que mexe no SLA do dia.
    const linhaAusencia = (a: AusenciaTrabalho, passada: boolean) => (
        <div key={a.id} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border ${passada
            ? 'bg-gray-50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-700/60'
            : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700'}`}
        >
            <div className="min-w-0">
                <span className={`text-xs font-bold ${passada ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>
                    {new Date(a.data + 'T12:00:00').toLocaleDateString('pt-BR')}
                    {!a.dia_inteiro && a.hora_inicio && a.hora_fim ? ` (${hhmm(a.hora_inicio)}–${hhmm(a.hora_fim)})` : ' (dia inteiro)'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 italic ml-2">{a.motivo || 'Sem motivo informado'}</span>
                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${a.origem === 'solicitacao' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                    {ORIGEM_LABEL[a.origem]}
                </span>
            </div>
            <button
                onClick={() => excluirAusencia(a.id)}
                disabled={excluindo === a.id}
                title={passada ? 'Remover (muda o SLA desse dia)' : 'Remover'}
                className="p-1 rounded text-gray-300 hover:text-red-500 shrink-0"
            >
                {excluindo === a.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            </button>
        </div>
    );

    const excluirAusencia = async (id: string) => {
        setExcluindo(id);
        try {
            const { error } = await supabase.from('ausencias_trabalho').delete().eq('id', id);
            if (error) throw error;
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'escalas', acao: 'excluir_ausencia', origem: 'acao' });
            onChanged();
        } catch (e: any) {
            addToast('Erro ao remover ausência: ' + (e.message || 'erro desconhecido'), 'error');
        } finally {
            setExcluindo(null);
        }
    };

    return (
        <div>
            <button
                onClick={() => setAberto(v => !v)}
                className="w-full px-6 py-4 flex items-center justify-between gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center shrink-0">
                        {(pessoa.full_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{pessoa.full_name}</p>
                        <p className="text-xs text-gray-400">{resumoEscala(dias)}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {ausenteHoje && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Ausente hoje</span>
                    )}
                    {semEscala && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">Sem escala definida</span>
                    )}
                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${aberto ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {aberto && (
                <div className="px-6 pb-6 pt-2 bg-gray-50/50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-5">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Jornada semanal</p>
                        <button
                            onClick={aplicarATodosOsDiasUteis}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                            title="Copia o horário de segunda para terça-sexta"
                        >
                            <Copy size={12} /> Aplicar horário de segunda a todos os dias úteis
                        </button>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                        {DIA_ORDEM.map(d => {
                            const dia = dias[d];
                            return (
                                <div key={d} className={`rounded-xl border p-2 space-y-1.5 ${dia.ativo ? 'border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-dashed border-gray-200 dark:border-gray-600 bg-gray-50/60 dark:bg-gray-700/30 opacity-60'}`}>
                                    <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-200">
                                        <input type="checkbox" checked={dia.ativo} onChange={e => atualizarDia(d, { ativo: e.target.checked })} className="rounded" />
                                        {DIA_LABEL[d]}
                                    </label>
                                    {dia.ativo && (
                                        <>
                                            <div className="flex items-center gap-1">
                                                <input type="time" value={dia.hora_inicio} onChange={e => atualizarDia(d, { hora_inicio: e.target.value })}
                                                    className="w-full text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-1 py-1" />
                                                <span className="text-[10px] text-gray-400">–</span>
                                                <input type="time" value={dia.hora_fim} onChange={e => atualizarDia(d, { hora_fim: e.target.value })}
                                                    className="w-full text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-1 py-1" />
                                            </div>
                                            <div className="border-t border-dashed border-gray-200 dark:border-gray-600 pt-1">
                                                <p className="text-[9px] text-gray-400 uppercase mb-0.5">Almoço</p>
                                                <div className="flex items-center gap-1">
                                                    <input type="time" value={dia.almoco_inicio} onChange={e => atualizarDia(d, { almoco_inicio: e.target.value })}
                                                        className="w-full text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-1 py-1" />
                                                    <span className="text-[10px] text-gray-400">–</span>
                                                    <input type="time" value={dia.almoco_fim} onChange={e => atualizarDia(d, { almoco_fim: e.target.value })}
                                                        className="w-full text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded px-1 py-1" />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10.5px] text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-primary" /> Trabalha</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-300" /> Folga</span>
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-secondary" /> Almoço</span>
                    </div>

                    <div className="flex justify-end">
                        <button onClick={salvar} disabled={salvando} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50">
                            {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar escala
                        </button>
                    </div>

                    <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ausências pontuais</p>
                            <button onClick={() => setShowModal(true)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold text-primary bg-primary/10 hover:bg-primary/20">
                                <Plus size={12} /> Adicionar
                            </button>
                        </div>
                        {ausenciasFuturas.length === 0 ? (
                            <p className="text-center text-xs text-gray-400 py-4">Nenhuma ausência de hoje em diante.</p>
                        ) : (
                            <div className="space-y-1.5">
                                {ausenciasFuturas.map(a => linhaAusencia(a, false))}
                            </div>
                        )}

                        {ausenciasPassadas.length > 0 && (
                            <div className="mt-3">
                                <button
                                    onClick={() => setShowPassadas(v => !v)}
                                    className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                                >
                                    <ChevronDown size={12} className={`transition-transform ${showPassadas ? 'rotate-180' : ''}`} />
                                    Já passaram ({ausenciasPassadas.length}) · últimos {DIAS_AUSENCIAS_PASSADAS} dias
                                </button>
                                {showPassadas && (
                                    <div className="space-y-1.5 mt-2">
                                        {ausenciasPassadas.map(a => linhaAusencia(a, true))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {historicoSolicitacoes.length > 0 && (
                        <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                            <button
                                onClick={() => setShowHistorico(v => !v)}
                                className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                                <ChevronDown size={12} className={`transition-transform ${showHistorico ? 'rotate-180' : ''}`} />
                                Ver decisões anteriores ({historicoSolicitacoes.length})
                            </button>
                            {showHistorico && (
                                <div className="mt-2 space-y-1.5">
                                    {historicoSolicitacoes.map(s => {
                                        const st = SOLIC_STATUS_LABEL[s.status] || { label: s.status, className: 'text-gray-500' };
                                        return (
                                            <div key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                                                <div className="min-w-0">
                                                    <span className="text-xs font-bold text-gray-800 dark:text-gray-100">
                                                        {new Date(s.data + 'T12:00:00').toLocaleDateString('pt-BR')}
                                                        {!s.dia_inteiro && s.hora_inicio && s.hora_fim ? ` (${hhmm(s.hora_inicio)}–${hhmm(s.hora_fim)})` : ' (dia inteiro)'}
                                                    </span>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400 italic ml-2">{s.motivo || 'Sem motivo informado'}</span>
                                                </div>
                                                <span className={`text-[11px] font-bold shrink-0 ${st.className}`}>{st.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {showModal && <AdicionarAusenciaModal onClose={() => setShowModal(false)} onConfirm={adicionarAusencia} salvando={salvando} />}
        </div>
    );
};

interface LeadsSemAtendimentoPanelProps {
    pessoas: Profile[];
    horarios: HorarioTrabalho[];
    ausencias: AusenciaTrabalho[];
}

interface AtribuicaoAberta {
    id: string;
    negociacao_id: number;
    responsavel_id: string;
    atribuido_em: string;
    cliente_nome: string | null;
}

/**
 * "O que está parado agora?" — leads atribuídos a Closers e BDRs que ainda
 * não tiveram nenhuma atividade humana (negociacao_atribuicoes com
 * primeira_atividade_em NULL e o período ainda aberto). Diferente do
 * SlaAtendimentoBlock (que só mostra médias históricas), isto é uma lista
 * acionável — cada linha abre a negociação. Não existe query pra isso hoje em
 * nenhuma tela; é nova, mas barata (dois IS NULL + in, sem agregação).
 *
 * O "há quanto tempo" usa tempoUtilDecorrido (mesmo cálculo do SLA útil no
 * SlaAtendimentoBlock) — não relógio corrido. Sem isso, um lead atribuído
 * 17h55 aparecia com "14h parado" às 8h do dia seguinte, mesmo sem ninguém
 * disponível pra atender entre 18h e o início do expediente (achado do
 * usuário, ago/2026: "está desconsiderando esses prazos" — o painel ainda não
 * descontava a madrugada/fora de expediente, diferente do resto do SLA).
 */
const LeadsSemAtendimentoPanel: React.FC<LeadsSemAtendimentoPanelProps> = ({ pessoas, horarios, ausencias }) => {
    const navigate = useNavigate();
    const [abertas, setAbertas] = useState<AtribuicaoAberta[]>([]);
    const [carregado, setCarregado] = useState(false);
    const [agora, setAgora] = useState(() => new Date());
    // Filtro por pessoa — mesma lista de gente do "Disponível agora" logo abaixo,
    // pra dar pra focar em quem está livre agora e ver se sobrou lead parado com ela.
    const [responsavelFiltro, setResponsavelFiltro] = useState<string | null>(null);

    const ids = useMemo(() => pessoas.map(p => p.id), [pessoas]);
    const nomesPorId = useMemo(() => new Map(pessoas.map(p => [p.id, p.full_name])), [pessoas]);
    const idsKey = ids.join(',');

    const horariosPorPessoa = useMemo(() => {
        const m = new Map<string, HorarioTrabalho[]>();
        for (const h of horarios) { if (!m.has(h.profile_id)) m.set(h.profile_id, []); m.get(h.profile_id)!.push(h); }
        return m;
    }, [horarios]);
    const ausenciasPorPessoa = useMemo(() => {
        const m = new Map<string, AusenciaTrabalho[]>();
        for (const a of ausencias) { if (!m.has(a.profile_id)) m.set(a.profile_id, []); m.get(a.profile_id)!.push(a); }
        return m;
    }, [ausencias]);

    useEffect(() => {
        if (ids.length === 0) { setAbertas([]); setCarregado(true); return; }
        let cancelado = false;
        (async () => {
            const { data, error } = await supabase
                .from('negociacao_atribuicoes')
                .select('id, negociacao_id, responsavel_id, atribuido_em, negociacoes(cliente_nome)')
                .in('responsavel_id', ids)
                .is('encerrado_em', null)
                .is('primeira_atividade_em', null)
                // Atribuição já desconsiderada do SLA por adm/gerente não entra aqui
                // tampouco. Sem isso, a transferência em massa de 02/09/2026 (558 leads
                // herdados do desligamento da responsável anterior, todos marcados com
                // motivo) enchia o painel com 346 linhas de "35h42min" — o mesmo tempo
                // em todas, porque o relógio conta desde o instante único da carga.
                // O que estava de fato parado no dia eram 1 lead, não 346.
                .eq('sla_desconsiderado', false)
                .order('atribuido_em', { ascending: true });
            if (cancelado) return;
            if (error) {
                console.error('Erro ao carregar leads sem atendimento:', error);
                setAbertas([]);
            } else {
                setAbertas((data || []).map((d: any) => ({
                    id: d.id,
                    negociacao_id: d.negociacao_id,
                    responsavel_id: d.responsavel_id,
                    atribuido_em: d.atribuido_em,
                    cliente_nome: d.negociacoes?.cliente_nome ?? null,
                })));
            }
            setCarregado(true);
        })();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    // Só recalcula os rótulos de tempo decorrido — não refaz a busca (a lista em
    // si muda pouco; o "há quanto tempo" é o que precisa se mover sozinho).
    useEffect(() => {
        const t = setInterval(() => setAgora(new Date()), 60_000);
        return () => clearInterval(t);
    }, []);

    if (!carregado || abertas.length === 0) return null; // nada parado é o estado normal — não polui a Visão Geral

    // Nível de cada linha, calculado uma vez e reaproveitado no badge do cabeçalho —
    // que reflete o pior caso da lista em vez de ficar sempre vermelho (senão o
    // header grita urgência mesmo quando tudo ali foi atribuído há 2 minutos).
    // Ordenado por tempo ÚTIL parado, não por atribuido_em — quem foi atribuído
    // sexta 17h59 tem pouco tempo útil decorrido até segunda de manhã, mesmo
    // "antigo" em relógio corrido; isso evitava que ele aparecesse como mais
    // urgente do que alguém atribuído segunda 9h05 e ainda sem toque às 11h.
    const linhas = abertas
        .map(a => {
            const decorridoMs = tempoUtilDecorrido(
                horariosPorPessoa.get(a.responsavel_id) || [],
                ausenciasPorPessoa.get(a.responsavel_id) || [],
                new Date(a.atribuido_em),
                agora,
            );
            const nivel = nivelUrgencia(decorridoMs);
            return { ...a, decorridoMs, nivel };
        })
        .sort((a, b) => b.decorridoMs - a.decorridoMs);

    // Opções do filtro vêm de quem TEM lead parado (não da lista inteira do
    // departamento) — sem gente com "0" poluindo os pills.
    const responsaveisComLead = Array.from(new Set(linhas.map(l => l.responsavel_id)))
        .map(id => ({ id, nome: nomesPorId.get(id) || 'Sem responsável' }))
        .sort((a, b) => a.nome.localeCompare(b.nome));

    const linhasFiltradas = responsavelFiltro ? linhas.filter(l => l.responsavel_id === responsavelFiltro) : linhas;

    const piorNivel: 'crit' | 'warn' | 'ok' = linhasFiltradas.some(l => l.nivel === 'crit') ? 'crit' : linhasFiltradas.some(l => l.nivel === 'warn') ? 'warn' : 'ok';
    const badgeClasse = piorNivel === 'crit'
        ? 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400'
        : piorNivel === 'warn'
            ? 'text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400'
            : 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">Leads sem atendimento</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Atribuídos e ainda sem nenhuma atividade — tempo útil, descontando fora do expediente.</p>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${badgeClasse}`}>{linhasFiltradas.length}</span>
            </div>

            {responsaveisComLead.length > 1 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    <button
                        onClick={() => setResponsavelFiltro(null)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${!responsavelFiltro ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                    >
                        Todos
                    </button>
                    {responsaveisComLead.map(r => (
                        <button
                            key={r.id}
                            onClick={() => setResponsavelFiltro(prev => prev === r.id ? null : r.id)}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${responsavelFiltro === r.id ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        >
                            {r.nome}
                        </button>
                    ))}
                </div>
            )}

            {linhasFiltradas.length === 0 && (
                <p className="text-center text-xs text-gray-400 py-4">Sem leads parados para esta pessoa.</p>
            )}

            <div className="space-y-1.5">
                {linhasFiltradas.map(a => {
                    const { decorridoMs, nivel } = a;
                    return (
                        <button
                            key={a.id}
                            onClick={() => navigate(`/crm?negociacaoId=${a.negociacao_id}`)}
                            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${nivel === 'crit'
                                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 hover:bg-red-100 dark:hover:bg-red-900/30'
                                : nivel === 'warn'
                                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                                    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-100 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                                }`}
                        >
                            <div className="min-w-0">
                                <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">{a.cliente_nome || `Negociação #${a.negociacao_id}`}</p>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400">{nomesPorId.get(a.responsavel_id) || 'Sem responsável'}</p>
                            </div>
                            <span className={`text-xs font-bold shrink-0 tabular-nums ${nivel === 'crit' ? 'text-red-600 dark:text-red-400' : nivel === 'warn' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                {fmtDecorrido(decorridoMs)}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

interface DisponibilidadeAgoraPanelProps {
    pessoas: Profile[];
    horarios: HorarioTrabalho[];
    ausencias: AusenciaTrabalho[];
}

/**
 * "Quem eu posso escalar agora?" — leitura em tempo real (não histórico) da
 * mesma regra que o rodízio de leads usa (fn_esta_disponivel no banco,
 * espelhada em estaDisponivel no cliente): jornada do dia + fora do almoço +
 * sem ausência cobrindo este minuto. Reaproveita os dados que
 * EscalasComercialView já busca — sem consulta nova.
 */
const DisponibilidadeAgoraPanel: React.FC<DisponibilidadeAgoraPanelProps> = ({ pessoas, horarios, ausencias }) => {
    const [agora, setAgora] = useState(() => new Date());

    // Recalcula a cada minuto — disponibilidade muda em limites de horário
    // (entrada, almoço, saída) que não avisam sozinhos.
    useEffect(() => {
        const id = setInterval(() => setAgora(new Date()), 60_000);
        return () => clearInterval(id);
    }, []);

    const linhas = useMemo(() => {
        return pessoas.map(p => {
            const h = horarios.filter(x => x.profile_id === p.id);
            const a = ausencias.filter(x => x.profile_id === p.id);
            const disponivel = estaDisponivel(h, a, agora);
            const semEscala = !h.some(x => x.ativo);
            // Pausa que a própria pessoa deu no botão do topo. Precisa aparecer
            // separada de "fora do horário": enquanto ela corre, o SLA de
            // atendimento não conta — e o total do dia é o preço disso.
            const pausada = !!pausaAberta(a, agora);
            const minutosPausados = minutosPausadosNoDia(a, agora);
            return { pessoa: p, disponivel, semEscala, pausada, minutosPausados };
        }).sort((a, b) => Number(b.disponivel) - Number(a.disponivel));
    }, [pessoas, horarios, ausencias, agora]);

    const disponiveisCount = linhas.filter(l => l.disponivel).length;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between gap-2 mb-4">
                <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">Disponível agora</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Mesma regra usada pelo rodízio de leads — atualiza sozinho a cada minuto.
                    </p>
                </div>
                <span className="text-xs font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full shrink-0">
                    {disponiveisCount} de {linhas.length} disponíveis
                </span>
            </div>

            {linhas.length === 0 ? (
                <p className="text-center text-xs text-gray-400 py-6">Ninguém com a função Closer ou BDR.</p>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {linhas.map(({ pessoa, disponivel, semEscala, pausada, minutosPausados }) => (
                        <div
                            key={pessoa.id}
                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border ${disponivel
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50'
                                : pausada
                                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50'
                                    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-100 dark:border-gray-700'
                                }`}
                            title={minutosPausados > 0 ? `Ficou inativa ${fmtDuracaoMin(minutosPausados)} hoje — esse tempo não conta no SLA.` : undefined}
                        >
                            <span className={`w-2 h-2 rounded-full shrink-0 ${disponivel ? 'bg-emerald-500' : pausada ? 'bg-amber-500' : semEscala ? 'bg-gray-300 dark:bg-gray-600' : 'bg-amber-400'}`} />
                            <div className="min-w-0">
                                <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">{pessoa.full_name}</p>
                                <p className={`text-[10.5px] font-semibold ${disponivel ? 'text-emerald-600 dark:text-emerald-400' : pausada ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}>
                                    {pausada
                                        ? `Inativa · ${fmtDuracaoMin(minutosPausados)}`
                                        : disponivel ? 'Disponível' : semEscala ? 'Sem escala' : 'Fora do horário'}
                                </p>
                                {!pausada && minutosPausados > 0 && (
                                    <p className="text-[10px] text-gray-400 dark:text-gray-500">{fmtDuracaoMin(minutosPausados)} inativa hoje</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/** Quem tem escala: Closers e BDRs, as funções do rodízio de leads (antes, o departamento Comercial Interno). */
const FUNCOES_COM_ESCALA = [...FUNCOES_COM_RODIZIO];

const EscalasComercialView: React.FC = () => {
    const { user } = useAuth();
    const { addToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [pessoas, setPessoas] = useState<Profile[]>([]);
    const [horarios, setHorarios] = useState<HorarioTrabalho[]>([]);
    const [ausencias, setAusencias] = useState<AusenciaTrabalho[]>([]);
    const [solicitacoes, setSolicitacoes] = useState<SolicitacaoAusencia[]>([]);
    const [busca, setBusca] = useState('');
    const [decidindo, setDecidindo] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    // Visão Geral = leitura pro gestor (SLA + quem está disponível agora);
    // Editar Escalas = a tela original, para montar a jornada de cada pessoa.
    const [aba, setAba] = useState<'geral' | 'escalas'>('geral');

    useEffect(() => {
        const carregar = async () => {
            // Só a primeira carga troca a tela pelo spinner. Recarregar depois de salvar
            // (escala, ausência, decisão) com spinner desmontava os painéis: a pessoa que o
            // gestor estava editando fechava e ele não via o que acabou de cadastrar.
            if (refreshKey === 0) setLoading(true);
            try {
                const { data: perfis, error: errPerfis } = await supabase
                    .from('profiles')
                    .select('*')
                    .in('funcao_comercial', FUNCOES_COM_ESCALA)
                    .neq('status', 'inactive')
                    .order('full_name');
                if (errPerfis) throw errPerfis;

                const ids = (perfis || []).map(p => p.id);
                setPessoas(perfis || []);

                if (ids.length > 0) {
                    const [{ data: h }, { data: a }, { data: s }] = await Promise.all([
                        supabase.from('horarios_trabalho').select('*').in('profile_id', ids),
                        supabase.from('ausencias_trabalho').select('*').in('profile_id', ids),
                        // Traz pendentes + já decididas (aprovada/rejeitada) — a fila abaixo filtra só
                        // as pendentes; o histórico por pessoa (PessoaPainel) usa o resto.
                        supabase.from('solicitacoes_ausencia').select('*, profile:profiles!solicitacoes_ausencia_profile_id_fkey(*)').in('profile_id', ids).order('created_at', { ascending: false }),
                    ]);
                    setHorarios((h || []) as HorarioTrabalho[]);
                    setAusencias((a || []) as AusenciaTrabalho[]);
                    setSolicitacoes((s || []) as SolicitacaoAusencia[]);
                } else {
                    setHorarios([]);
                    setAusencias([]);
                    setSolicitacoes([]);
                }
            } catch (e) {
                console.error('Erro ao carregar escalas:', e);
            } finally {
                setLoading(false);
            }
        };
        carregar();
    }, [refreshKey]);

    const recarregar = () => setRefreshKey(k => k + 1);

    const decidirSolicitacao = async (sol: SolicitacaoAusencia, aprovar: boolean) => {
        if (!user) return;
        setDecidindo(sol.id);
        try {
            if (aprovar) {
                const { error } = await supabase.rpc('fn_aprovar_solicitacao_ausencia', { p_solicitacao_id: sol.id, p_decisor_id: user.id });
                if (error) throw error;
                addToast('Ausência aprovada.', 'success');
                logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'escalas', acao: 'decidir_solicitacao_ausencia', origem: 'acao' });
            } else {
                const { error } = await supabase.from('solicitacoes_ausencia').update({ status: 'rejeitada', decidido_por: user.id, decidido_em: new Date().toISOString() }).eq('id', sol.id);
                if (error) throw error;
                logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'escalas', acao: 'decidir_solicitacao_ausencia', origem: 'acao' });
                addToast('Solicitação rejeitada.', 'success');
            }
            recarregar();
        } catch (e: any) {
            addToast('Erro ao decidir solicitação: ' + (e.message || 'erro desconhecido'), 'error');
        } finally {
            setDecidindo(null);
        }
    };

    const pessoasFiltradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return pessoas.filter(p => !termo || (p.full_name || '').toLowerCase().includes(termo));
    }, [pessoas, busca]);

    const solicitacoesPendentes = useMemo(() => solicitacoes.filter(s => s.status === 'pendente'), [solicitacoes]);

    const semEscalaCount = useMemo(() => {
        return pessoas.filter(p => !horarios.some(h => h.profile_id === p.id && h.ativo)).length;
    }, [pessoas, horarios]);

    if (loading) return <div className="flex items-center justify-center h-64"><LoadingSpinner label="Carregando escalas..." /></div>;

    return (
        <div className="animate-fade-in pb-12">
            <div className="mb-6">
                <h1 className="text-2xl font-bold font-poppins text-gray-900 dark:text-white mb-2">Escalas — Comercial</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Dias, horários e ausências de cada pessoa do time. Usado pra distribuir leads automaticamente — se alguém está de almoço ou ausente, o lead cai pra outra pessoa disponível.
                </p>
            </div>

            {pessoas.length > 0 && semEscalaCount > 0 && (
                <div className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl px-4 py-3 mb-6">
                    <AlertTriangle size={18} className="text-amber-500 shrink-0" />
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                        <b>{semEscalaCount} de {pessoas.length}</b> pessoa{semEscalaCount > 1 ? 's' : ''} sem escala cadastrada — enquanto isso, o rodízio de leads ignora disponibilidade pra ela{semEscalaCount > 1 ? 's' : ''} (comportamento atual, sem filtro).
                    </p>
                </div>
            )}

            <div className="flex items-center gap-2 mb-6">
                <button
                    onClick={() => setAba('geral')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full transition-all ${aba === 'geral' ? 'bg-primary text-white shadow-sm' : 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                >
                    <LayoutGrid size={14} /> Visão Geral
                </button>
                <button
                    onClick={() => setAba('escalas')}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-full transition-all ${aba === 'escalas' ? 'bg-primary text-white shadow-sm' : 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                >
                    <ListChecks size={14} /> Editar Escalas
                </button>
            </div>

            {solicitacoesPendentes.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Solicitações de ausência pendentes</h2>
                    <div className="space-y-2">
                        {solicitacoesPendentes.map(s => (
                            <div key={s.id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-gray-800 dark:text-gray-100">
                                        {s.profile?.full_name || 'Alguém'} · {new Date(s.data + 'T12:00:00').toLocaleDateString('pt-BR')}
                                        {!s.dia_inteiro ? ` (${hhmm(s.hora_inicio)}–${hhmm(s.hora_fim)})` : ' (dia inteiro)'}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">{s.motivo || 'Sem motivo informado'}</p>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button onClick={() => decidirSolicitacao(s, false)} disabled={decidindo === s.id} className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50" title="Rejeitar">
                                        <XCircle size={16} />
                                    </button>
                                    <button onClick={() => decidirSolicitacao(s, true)} disabled={decidindo === s.id} className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50" title="Aprovar">
                                        {decidindo === s.id ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {aba === 'geral' ? (
                <>
                    <LeadsSemAtendimentoPanel pessoas={pessoas} horarios={horarios} ausencias={ausencias} />
                    <DisponibilidadeAgoraPanel pessoas={pessoas} horarios={horarios} ausencias={ausencias} />
                    <SlaAtendimentoBlock />
                </>
            ) : (
                <>
                    <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center gap-2 mb-4">
                        <Search size={15} className="text-gray-400 ml-1" />
                        <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar pessoa..."
                            className="flex-1 bg-transparent outline-none text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400" />
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm divide-y divide-gray-100 dark:divide-gray-700">
                        {pessoasFiltradas.length === 0 ? (
                            <p className="text-center text-sm text-gray-400 py-10">
                                {pessoas.length === 0 ? 'Ninguém com a função Closer ou BDR.' : 'Ninguém encontrado.'}
                            </p>
                        ) : (
                            pessoasFiltradas.map(p => (
                                <PessoaPainel
                                    key={p.id}
                                    pessoa={p}
                                    horarios={horarios.filter(h => h.profile_id === p.id)}
                                    ausencias={ausencias.filter(a => a.profile_id === p.id)}
                                    solicitacoes={solicitacoes.filter(s => s.profile_id === p.id)}
                                    onChanged={recarregar}
                                />
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default EscalasComercialView;
